import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, CreditCard, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { nexiService, type PagamentoInSospeso } from '../../services/nexiService';
import { useAuth } from '../../context/AuthContext';

/**
 * Avviso di un pagamento con carta lasciato a metà. Entro 15 minuti dalla
 * creazione il Cliente lo completa sullo **stesso** ordine, senza crearne un
 * altro; dopo, l'ordine viene annullato e si riparte dal checkout. Se cambia
 * il carrello, l'ordine in sospeso viene annullato al prossimo pagamento.
 */
export function PendingPaymentBanner({ compact = false }: { compact?: boolean }) {
  const { user, canPlaceOrder } = useAuth();
  const navigate = useNavigate();
  const [pagamento, setPagamento] = useState<PagamentoInSospeso | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || !canPlaceOrder) return;
    let cancelled = false;
    nexiService.inSospeso()
      .then(p => { if (!cancelled) setPagamento(p); })
      .catch(() => { /* nessun avviso: il checkout resta usabile */ });
    return () => { cancelled = true; };
  }, [user, canPlaceOrder]);

  // Allo scadere sparisce da solo: l'ordine non è più riprendibile.
  useEffect(() => {
    if (!pagamento) return;
    const ms = new Date(pagamento.scadeAlle).getTime() - Date.now();
    if (ms <= 0) { setPagamento(null); return; }
    const t = setTimeout(() => setPagamento(null), ms);
    return () => clearTimeout(t);
  }, [pagamento]);

  if (!pagamento) return null;

  const scade = new Date(pagamento.scadeAlle).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  const importo = (pagamento.amountCents / 100).toFixed(2).replace('.', ',');

  const completa = async () => {
    setBusy(true);
    try {
      const avvio = await nexiService.riprendi(pagamento.codTrans);
      navigate('/pagamento/redirect', { state: { action: avvio.action, fields: avvio.fields } });
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail?.codTrans) {
        navigate(`/pagamento/esito?codTrans=${detail.codTrans}`);
        return;
      }
      toast.error((typeof detail === 'string' && detail) || 'Il pagamento non è più riprendibile: ripeti il checkout.');
      setPagamento(null);
      setBusy(false);
    }
  };

  return (
    <div
      className={`rounded-xl border border-amber-200 bg-amber-50 ${compact ? 'p-3' : 'p-4'} flex flex-wrap items-center gap-3`}
      data-testid="pending-payment-banner"
    >
      <Clock className="w-4 h-4 text-amber-600 flex-shrink-0" />
      <div className="flex-1 min-w-[200px] text-sm text-amber-900">
        <p className="font-semibold">
          Pagamento con carta in sospeso{(pagamento.numeroGestionale ?? pagamento.numeroOrdine) ? ` per l'ordine ${pagamento.numeroGestionale ?? pagamento.numeroOrdine}` : ''} (€ {importo})
        </p>
        <p className="text-xs mt-0.5">
          Puoi completarlo fino alle {scade}. Se modifichi il carrello, quell&apos;ordine verrà annullato e ne verrà creato uno nuovo.
        </p>
      </div>
      <button
        type="button"
        onClick={completa}
        disabled={busy}
        className="h-9 px-4 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60"
        data-testid="pending-payment-resume"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
        Completa il pagamento
      </button>
    </div>
  );
}
